import { api } from "@/api"
import { BASE_URL, ENDPOINTS } from "@/constants"
import { toast } from "@/hooks/use-toast"
import { getApiErrorMessage } from "@/lib/api-error"
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@/libs"
import type {
  ReprocessResult,
  WaitingWithPaidTransactionResponse,
} from "@/types/admin-reprocess"

// Chave base das queries da feature — usada também na invalidação.
export const ADMIN_REPROCESS_QUERY_KEY =
  ENDPOINTS.ADMIN_REPROCESS_WAITING_WITH_PAID_TRANSACTION

// Lista os pagamentos presos (waiting com transação já paga).
export const getWaitingWithPaidTransaction = async (
  page: number,
  size: number
): Promise<WaitingWithPaidTransactionResponse> => {
  const url = `${BASE_URL}${ENDPOINTS.ADMIN_REPROCESS_WAITING_WITH_PAID_TRANSACTION}?page=${page}&size=${size}`
  const { data } =
    await api.get<WaitingWithPaidTransactionResponse>(url)
  return data
}

// Opções da query de casos presos (exportadas p/ teste).
//
// Por que os overrides abaixo existem (skeleton de carregamento):
// - `gcTime: 0` — o QueryClient da app é um singleton com cache; sem isso,
//   ao REENTRAR na página o dado da visita anterior (inclusive lista vazia →
//   "Nenhum caso encontrado") renderiza na hora e `isLoading` nunca mais fica
//   true: o refetch do `refetchOnMount: "always"` roda em background exibindo
//   estado velho em vez do skeleton. Com `gcTime: 0` o cache é descartado ao
//   sair da página e toda entrada volta a ser um carregamento inicial
//   (isLoading → skeleton). O refetch com a página ABERTA não é afetado
//   (a query segue ativa); na UI o refetch também exibe skeleton via
//   isFetching (ver resolveTableBodyState em pages/admin/table-state.ts).
// - `placeholderData: undefined` — anula o default global
//   `placeholderData: keepPreviousData` (src/providers/index.tsx), que
//   mascararia o estado pending (isLoading nunca true) caso a paginação
//   desta listagem vire dinâmica.
export const waitingWithPaidTransactionQueryOptions = (
  page: number,
  size: number
) => ({
  queryKey: [
    ADMIN_REPROCESS_QUERY_KEY,
    page,
    size,
  ] as const,
  queryFn: async () =>
    getWaitingWithPaidTransaction(page, size),
  refetchOnMount: "always" as const,
  gcTime: 0,
  placeholderData: undefined,
})

export const useWaitingWithPaidTransaction = (
  page: number,
  size: number
) => {
  return useQuery(
    waitingWithPaidTransactionQueryOptions(page, size)
  )
}

// Reprocessa apenas os orderIds selecionados.
export const useReprocessSelectedMutation = () => {
  const queryClient = useQueryClient()

  return useMutation<ReprocessResult, unknown, string[]>({
    mutationFn: async (orderIds: string[]) => {
      const url = `${BASE_URL}${ENDPOINTS.ADMIN_REPROCESS_PRE_AUTHORIZED}`
      const { data } = await api.post<ReprocessResult>(
        url,
        { orderIds }
      )
      return data
    },
    onSuccess: async (result) => {
      toast({
        title: `Reprocessados ${result.reprocessed.length}/${result.total} pagamentos`,
        variant: "success",
      })
      await queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === ADMIN_REPROCESS_QUERY_KEY,
      })
    },
    onError: (error: unknown) => {
      toast({
        title: getApiErrorMessage(
          error,
          "Erro ao reprocessar pagamentos selecionados"
        ),
        variant: "destructive",
      })
    },
  })
}

// Reprocessa todos os casos presos.
export const useReprocessAllMutation = () => {
  const queryClient = useQueryClient()

  return useMutation<ReprocessResult, unknown, void>({
    mutationFn: async () => {
      const url = `${BASE_URL}${ENDPOINTS.ADMIN_REPROCESS_ALL}`
      const { data } = await api.post<ReprocessResult>(url)
      return data
    },
    onSuccess: async (result) => {
      toast({
        title: `Reprocessados ${result.reprocessed.length}/${result.total} pagamentos`,
        variant: "success",
      })
      await queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === ADMIN_REPROCESS_QUERY_KEY,
      })
    },
    onError: (error: unknown) => {
      toast({
        title: getApiErrorMessage(
          error,
          "Erro ao reprocessar todos os pagamentos"
        ),
        variant: "destructive",
      })
    },
  })
}
